import React, { useRef } from "react";
import ReactDOM from 'react-dom';
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../Button";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import {
  QR_BRAND_COLOR,
  QR_BG_COLOR,
  DEFAULT_HIGH_RES_SIZE,
  downloadCanvasAsPng,
  getLogoDimensions,
} from "../../utils/qrCodeGenerator";

interface QRPreviewModalProps {
  open: boolean;
  promotionName: string;
  trackingId: string;
  qrUrl: string;
  onClose: () => void;
}

const QRPreviewModal: React.FC<QRPreviewModalProps> = ({
  open,
  promotionName,
  trackingId,
  qrUrl,
  onClose,
}) => {
  const previewCanvasRef = useRef<HTMLDivElement>(null);
  const highResCanvasWrapperRef = useRef<HTMLDivElement>(null);

  const logoSrc = `${process.env.PUBLIC_URL || ""}/TYI_ICON_GREEN.png`;
  const previewLogo = getLogoDimensions(64);
  const highResLogo = getLogoDimensions(1024);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      showSuccessToast("Scan URL copied to clipboard");
    } catch {
      showErrorToast("Could not copy URL. Please copy it manually.");
    }
  };

  const handleDownload = () => {
    const filenameBase = `promotion-${trackingId || "qr"}`;

    const canvas = highResCanvasWrapperRef.current?.querySelector("canvas") as
      | HTMLCanvasElement
      | null;

    if (!canvas) {
      const fallbackCanvas = previewCanvasRef.current?.querySelector("canvas") as
        | HTMLCanvasElement
        | null;
      if (!fallbackCanvas) {
        showErrorToast("Could not generate QR image");
        return;
      }
      downloadCanvasAsPng(fallbackCanvas, `${filenameBase}.png`);
      showSuccessToast("QR downloaded");
      return;
    }

    downloadCanvasAsPng(canvas, `${filenameBase}.png`);
    showSuccessToast("QR downloaded");
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/50 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          QR Code Preview
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{promotionName}</p>

        <div className="mt-5 flex flex-col items-center gap-4">
          <div
            ref={previewCanvasRef}
            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10"
          >
            <QRCodeCanvas
              value={qrUrl}
              size={240}
              marginSize={4}
              level="H"
              fgColor={QR_BRAND_COLOR}
              bgColor={QR_BG_COLOR}
              imageSettings={{
                src: logoSrc,
                height: previewLogo.height,
                width: previewLogo.width,
                excavate: true,
              }}
            />
          </div>

          <div className="w-full">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Tracking ID
            </p>
            <p className="break-all rounded-md bg-gray-100 px-3 py-2 font-mono text-sm text-gray-800 dark:bg-slate-800 dark:text-gray-100">
              {trackingId}
            </p>
          </div>

          <div className="w-full">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Scan URL
            </p>
            <p className="break-all rounded-md bg-gray-100 px-3 py-2 font-mono text-xs text-gray-700 dark:bg-slate-800 dark:text-gray-200">
              {qrUrl}
            </p>
          </div>
        </div>

        {/* Hidden offscreen 4096px render container */}
        <div style={{ position: "absolute", left: -99999, top: -99999, visibility: "hidden" }}>
          <div ref={highResCanvasWrapperRef}>
            <QRCodeCanvas
              value={qrUrl}
              size={DEFAULT_HIGH_RES_SIZE}
              marginSize={4}
              level="H"
              fgColor={QR_BRAND_COLOR}
              bgColor={QR_BG_COLOR}
              imageSettings={{
                src: logoSrc,
                height: highResLogo.height,
                width: highResLogo.width,
                excavate: true,
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="tertiary" onClick={handleCopy}>
            Copy URL
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            Download QR
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QRPreviewModal;
